import React, { act, useCallback, useEffect, useMemo, useState } from "react";
import Styles from "./LegerViewType.module.css";
import { useDispatch, useSelector } from "react-redux";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
} from "../../../commons/Accordion";
import { formatDate, formatSlotTime } from "../../../helpers/dateFormat";
import { AntSwitch } from "./LegerViewType";
import { USER_TYPES } from "../LedgerPageDashboardActive/LedgerPageDashboardActive";
import CallMadeIcon from "@mui/icons-material/CallMade";
import { fetchSchedule } from "../../../Redux/Slices/scheduleSlice";
import { ErrorMessage } from "../../../commons/Toast/ErrorMessage";
import { withdrawTerms } from "../../../Services/BuySell/Listing";
import {
  fetchActiveConnection,
  fetchMainScreenActiveConnection,
  fetchSelfCapacity,
  fetchSelfCapacityById,
} from "../../../Redux/Slices/CapacitySlice";
import ViewFinalizeTerms from "../../Modals/ConGen/ViewFinalizeTerms/ViewFinalizeTerms";
import AcceptTermsCondition from "../../Modals/ConGen/AcceptTermsCondition/AcceptTermsCondition";
import Charges from "../../Modals/ConGen/Charges/Charges";
import ViewConsent from "../../Modals/ConGen/ViewConsent/ViewConsent";
import UploadDocModal from "../../Modals/ConGen/UploadFiles/UploadFiles";
import ScheduleModal from "../../Modals/Schedule/ScheduleModal";
import {
  STATUSES,
  useConnectionData,
  useModals,
} from "../../ConsumerGenerator/ListingContainer/AvailableListing/AvailableListing";
import { useLocation, useNavigate } from "react-router-dom";
import UploadLDC from "../../Modals/ConGen/UploadLCD/UploadLDC";
import UploadConsent from "../../Modals/ConGen/UploadConsent/UploadConsent";
import { CircularProgress } from "@mui/material";
import ConfirmationModal from "../../../commons/ConfirmationModal/ConfirmationModal";
import { openBilateralChat } from "../../Modals/Banking/CreateEdit/helpers";

// Left Side Buttons Component
const LeftSideBtns = React.memo(({ clickedIndex }) => {
  const dispatch = useDispatch();
  const { user, finFinal, mainScreenMatchData } = useConnectionData();
  const { modals, toggleModal } = useModals();
  const [withdrawLoading, setWithdrawLoading] = useState(false);
  const listingId = useSelector(
    (state) => state?.CapacitySlice?.activeListingId
  );

  const connection = useMemo(() => {
    return mainScreenMatchData?.[clickedIndex];
  }, [mainScreenMatchData, clickedIndex]);

  const handleViewSchedule = useCallback(async () => {
    if (!connection?.id) return;

    dispatch(fetchSchedule(connection.id));
    setTimeout(() => {
      toggleModal("schedule", true);
    }, 1000);
  }, [connection?.id, dispatch, toggleModal]);

  const buttonConfig = useMemo(() => {
    const shouldShowViewTerms = () => {
      return (
        (user === USER_TYPES.CONSUMER &&
          connection?.conTermsAcceptanceStatus) ||
        (user === USER_TYPES.GENERATOR && connection?.genTermsAcceptanceStatus)
      );
    };

    const shouldShowViewWithdrawTerms = () => {
      return (
        ((user === USER_TYPES.CONSUMER &&
          connection?.conTermsAcceptanceStatus) ||
          (user === USER_TYPES.GENERATOR &&
            connection?.genTermsAcceptanceStatus)) &&
        connection?.status === STATUSES.PENDING
      );
    };

    const shouldShowViewCharges = () => {
      return finFinal?.status !== STATUSES.TERMS_INITIATED;
    };

    const shouldShowViewDocs = () => {
      return (
        (user === USER_TYPES.GENERATOR &&
          connection?.genAgreementAcceptanceStatus) ||
        (user === USER_TYPES.CONSUMER &&
          connection?.conAgreementAcceptanceStatus)
      );
    };

    const shouldShowViewConsent = () => {
      return (
        (user === USER_TYPES.CONSUMER && connection?.conConsentDocId) ||
        (user === USER_TYPES.GENERATOR && connection?.genConsentDocId)
      );
    };

    const shouldShowViewSchedule = () => {
      return connection?.status === STATUSES.SCHEDULED;
    };

    return [
      {
        show: shouldShowViewTerms(),
        onClick: () => toggleModal("terms", true),
        text: "View Terms",
      },
      // {
      //   show: shouldShowViewWithdrawTerms(),
      //   onClick: () => toggleModal("withdrawTerms", true),
      //   text: "View Terms",
      // },
      {
        show: shouldShowViewCharges(),
        onClick: () => toggleModal("charges", true),
        text: "View Charges",
      },
      {
        show: shouldShowViewDocs(),
        onClick: () => toggleModal("docs", true),
        text: "View Docs",
      },
      {
        show: shouldShowViewConsent(),
        onClick: () => toggleModal("consent", true),
        text: "View Consent",
      },
      {
        show: shouldShowViewSchedule(),
        onClick: handleViewSchedule,
        text: "View Schedule",
      },
    ];
  }, [user, connection, finFinal?.status, toggleModal, handleViewSchedule]);

  if (!connection) {
    return null;
  }

  function getAllContractIds(data) {
    const ids = [];

    // Top-level contractId (if present)
    if (data?.capacityConsumer?.contractId !== undefined) {
      ids.push(data.capacityConsumer.contractId);
    }
    if (data?.capacityGenerator?.contractId !== undefined) {
      ids.push(data.capacityGenerator.contractId);
    }

    // From matches.generators
    if (Array.isArray(data?.matches)) {
      data.matches.forEach((match) => {
        (match.generators || []).forEach((gen) => {
          if (gen.contractId !== undefined) {
            ids.push(gen.contractId);
          }
        });
        (match.consumers || []).forEach((con) => {
          if (con.contractId !== undefined) {
            ids.push(con.contractId);
          }
        });
      });
    }

    // Remove duplicates
    return Array.from(new Set(ids));
  }

  async function handleWithdraw() {
    const finalizedIds = getAllContractIds(connection);
    if (finalizedIds?.length === 0) {
      return ErrorMessage("Please Select Requisition(s)");
    }
    let payload = {
      contractIds: finalizedIds,
      capacityId: listingId,
    };
    try {
      setWithdrawLoading(true);
      const res = await withdrawTerms(payload);
      if (res?.data?.code === 200) {
        dispatch(
          fetchActiveConnection({
            curr: 0,
            capId: listingId,
            isTermsAccepted: null,
          })
        );
        dispatch(fetchSelfCapacity({ user }));
        toggleModal("withdrawTerms", false);
      }
    } catch (error) {
      console.error("Error withdrawing terms:", error);
    } finally {
      setWithdrawLoading(false);
    }
  }

  return (
    <>
      {/* Modals */}
      {modals.terms && (
        <ViewFinalizeTerms
          open={modals.terms}
          handleClose={() => toggleModal("terms", false)}
          data={connection}
        />
      )}
      {modals.withdrawTerms && (
        <AcceptTermsCondition
          open={modals.withdrawTerms}
          handleClose={() => toggleModal("withdrawTerms", false)}
          data={connection}
          // handleTermsSubmit={handleTermsSubmit}
          wholeData={connection}
          handleWithdraw={handleWithdraw}
          withdrawLoading={withdrawLoading}
        />
      )}

      {modals.charges && (
        <Charges
          open={modals.charges}
          onClose={() => toggleModal("charges", false)}
          contractId={connection?.id}
        />
      )}

      {modals.consent && (
        <ViewConsent
          connectionId={connection.id}
          open={modals.consent}
          onClose={() => toggleModal("consent", false)}
        />
      )}

      {modals.docs && (
        <UploadDocModal
          // agreementStatus={
          //   (user === "consumer" && connection?.genAgreementAcceptanceStatus) ||
          //   (user === "generator" && connection?.conAgreementAcceptanceStatus)
          // }
          type="view"
          connectionId={connection.id}
          open={modals.docs}
          handleClose={() => toggleModal("docs", false)}
        />
      )}

      {modals.schedule && (
        <ScheduleModal
          connectionId={connection.id}
          open={modals.schedule}
          handleClose={() => toggleModal("schedule", false)}
        />
      )}

      {/* Buttons */}
      <div className={Styles.leftSideBtnContainer}>
        {buttonConfig
          .filter((button) => button.show)
          .map((button, index) => (
            <button
              key={`left-btn-${index}`}
              onClick={button.onClick}
              className={Styles.leftSideBtn}
            >
              {button.text}
              <CallMadeIcon
                style={{
                  fontSize: "8px",
                  position: "absolute",
                  top: "-0px",
                }}
              />
            </button>
          ))}
      </div>
    </>
  );
});

// Right Side Buttons Component
const RightSideBtn = React.memo(
  ({
    clickedIndex,
    activeListingStatus,
    result,
    setTermsModal,
    isEditTerms,
    setIsEditTerms,
  }) => {
    const { user, mainScreenMatchData } = useConnectionData();
    const { modals, toggleModal } = useModals();
    const dispatch = useDispatch();
    const locationUrl = useLocation();
    const listingId = locationUrl?.search?.split("=")[1];
    const [withdrawLoading, setWithdrawLoading] = useState(false);

    const connection = useMemo(() => {
      return mainScreenMatchData?.[clickedIndex];
    }, [mainScreenMatchData, clickedIndex]);

    const conditionalHelpers = useMemo(() => {
      const shouldWithdraw = () => {
        return (
          (activeListingStatus === "TERMS_INITIATED" &&
            connection.status === "PENDING" &&
            user === "consumer") ||
          (activeListingStatus === "TERMS_INITIATED" &&
            connection.status === "PENDING" &&
            user === "generator")
        );
      };
      const shouldShowUploadDocs = () => {
        return (
          connection?.status === STATUSES.TERMS_ACCEPTED &&
          connection?.conTermsAcceptanceStatus &&
          connection?.genTermsAcceptanceStatus &&
          !connection?.conAgreementAcceptanceStatus &&
          !connection?.genAgreementAcceptanceStatus
        );
      };

      const shouldShowWaitingDocApproval = () => {
        return (
          (user === USER_TYPES.CONSUMER &&
            connection?.conAgreementAcceptanceStatus &&
            !connection?.genAgreementAcceptanceStatus) ||
          (user === USER_TYPES.GENERATOR &&
            connection?.genAgreementAcceptanceStatus &&
            !connection?.conAgreementAcceptanceStatus)
        );
      };

      const shouldShowWaitingTermsApproval = () => {
        return (
          ((user === USER_TYPES.CONSUMER &&
            connection?.conTermsAcceptanceStatus) ||
            (user === USER_TYPES.GENERATOR &&
              connection?.genTermsAcceptanceStatus)) &&
          connection?.status === STATUSES.PENDING
        );
      };

      const shouldShowTermsApproved = () => {
        return (
          connection?.status === STATUSES.TERMS_INITIATED &&
          user === USER_TYPES.CONSUMER &&
          connection?.genTermsAcceptanceStatus &&
          connection?.conTermsAcceptanceStatus
        );
      };

      const shouldShowWaitingOtherParties = () => {
        return (
          connection?.status === STATUSES.TERMS_INITIATED &&
          user === USER_TYPES.GENERATOR &&
          connection?.genTermsAcceptanceStatus &&
          connection?.conTermsAcceptanceStatus
        );
      };

      const shouldShowWaitingConsentUpload = () => {
        return (
          (user === USER_TYPES.CONSUMER &&
            connection?.conConsentDocId &&
            !connection?.genConsentDocId) ||
          (user === USER_TYPES.GENERATOR &&
            connection?.genConsentDocId &&
            !connection?.conConsentDocId)
        );
      };

      const shouldShowApproveDocs = () => {
        return (
          ((user === USER_TYPES.CONSUMER &&
            connection?.genAgreementAcceptanceStatus) ||
            (user === USER_TYPES.GENERATOR &&
              connection?.conAgreementAcceptanceStatus)) &&
          connection?.status !== STATUSES.AGREEMENT_ACCEPTED &&
          connection?.status !== STATUSES.SCHEDULED
        );
      };

      const shouldShowUploadConsent = () => {
        return (
          connection?.status === STATUSES.AGREEMENT_ACCEPTED &&
          !(
            (user === USER_TYPES.CONSUMER && connection?.conConsentDocId) ||
            (user === USER_TYPES.GENERATOR && connection?.genConsentDocId)
          )
        );
      };

      const shouldShowGenerateSchedule = () => {
        return (
          connection?.status === STATUSES.AGREEMENT_ACCEPTED &&
          connection?.conConsentDocId &&
          connection?.genConsentDocId &&
          connection?.status !== STATUSES.SCHEDULED
        );
      };

      return {
        shouldShowUploadDocs,
        shouldShowWaitingDocApproval,
        shouldShowWaitingTermsApproval,
        shouldShowTermsApproved,
        shouldShowWaitingOtherParties,
        shouldShowWaitingConsentUpload,
        shouldShowApproveDocs,
        shouldShowUploadConsent,
        shouldShowGenerateSchedule,
        shouldWithdraw,
      };
    }, [
      activeListingStatus,
      connection?.conAgreementAcceptanceStatus,
      connection?.conConsentDocId,
      connection?.conTermsAcceptanceStatus,
      connection?.genAgreementAcceptanceStatus,
      connection?.genConsentDocId,
      connection?.genTermsAcceptanceStatus,
      connection.status,
      user,
    ]);

    const statusMessages = useMemo(
      () => [
        {
          show: conditionalHelpers.shouldShowWaitingDocApproval(),
          text: "Waiting Document Approval",
        },
        {
          show: conditionalHelpers.shouldShowWaitingTermsApproval(),
          text: "Waiting Terms Approval",
        },
        {
          show: conditionalHelpers.shouldShowTermsApproved(),
          text: "Terms Approved",
        },
        {
          show: conditionalHelpers.shouldShowWaitingOtherParties(),
          text: "Waiting for other parties",
        },
        {
          show: conditionalHelpers.shouldShowWaitingConsentUpload(),
          text: "Waiting Consent Upload",
        },
      ],
      [conditionalHelpers]
    );

    const handleWithdraw = useCallback(
      async (id) => {
        const finalizedIds = [id];
        if (finalizedIds?.length === 0) {
          return ErrorMessage("Please Select Requisition(s)");
        }
        let payload = {
          contractIds: finalizedIds,
          capacityId: listingId,
        };

        try {
          setWithdrawLoading(true);
          const res = await withdrawTerms(payload, true);
          if (res?.data?.code === 200) {
            dispatch(
              fetchActiveConnection({
                curr: 0,
                capId: listingId,
                isTermsAccepted: null,
              })
            );
            dispatch(fetchMainScreenActiveConnection({ capId: listingId }));
            dispatch(fetchSelfCapacity({ user }));
            dispatch(fetchSelfCapacityById({ user, id: listingId }));
            toggleModal("withdrawTerms", false);
          }
        } catch (error) {
          console.error("Error withdrawing terms:", error);
        } finally {
          setWithdrawLoading(false);
        }
      },
      [dispatch, listingId, user, toggleModal]
    );

    const buttons = useMemo(
      () => [
        // {
        //   show: conditionalHelpers.shouldShowWaitingTermsApproval(),
        //   onClick: () => {
        //     setIsEditTerms(true);
        //     setTermsModal(true);
        //   },
        //   text: "Edit Terms",
        // },
        {
          show: conditionalHelpers.shouldWithdraw(),
          onClick: () => {
            toggleModal("withdrawTerms", true);
          },
          text: "Withdraw",
        },

        {
          show: conditionalHelpers.shouldShowUploadDocs(),
          onClick: () => toggleModal("uploadFiles", true),
          text: "Upload Docs",
        },
        {
          show: conditionalHelpers.shouldShowApproveDocs(),
          onClick: () => toggleModal("approveDocs", true),
          text: "Approve Docs",
        },
        {
          show: conditionalHelpers.shouldShowUploadConsent(),
          onClick: () => toggleModal("uploadConsent", true),
          text: "Upload Consent",
        },
        {
          show: conditionalHelpers.shouldShowGenerateSchedule(),
          onClick: () => toggleModal("uploadLDC", true),
          text: "Generate Schedule",
        },
      ],
      [conditionalHelpers, toggleModal]
    );

    if (!connection) {
      return null;
    }

    return (
      <>
        {/* Modals */}
        {modals.terms && (
          <ViewFinalizeTerms
            open={modals.terms}
            handleClose={() => toggleModal("terms", false)}
            data={connection}
            update={true}
          />
        )}
        {modals.uploadFiles && (
          <UploadDocModal
            data={connection}
            connectionId={connection.id}
            open={modals.uploadFiles}
            handleClose={() => toggleModal("uploadFiles", false)}
          />
        )}

        {modals.withdrawTerms && (
          <ConfirmationModal
            open={modals.withdrawTerms}
            handleClose={() => toggleModal("withdrawTerms", false)}
            handleSubmit={() => handleWithdraw(connection.id)}
            header={"Withdraw Terms Confirmation"}
            data={"Are you sure you want to withdraw terms?"}
            loading={withdrawLoading}
          />
        )}

        {modals.approveDocs && (
          <UploadDocModal
            agreementStatus={
              (user === "consumer" &&
                connection?.genAgreementAcceptanceStatus) ||
              (user === "generator" && connection?.conAgreementAcceptanceStatus)
            }
            data={connection}
            connectionId={connection.id}
            open={modals.approveDocs}
            handleClose={() => toggleModal("approveDocs", false)}
          />
        )}

        {modals.uploadLDC && (
          <UploadLDC
            connectionId={connection.id}
            connection={connection}
            open={modals.uploadLDC}
            onClose={() => toggleModal("uploadLDC", false)}
          />
        )}

        {modals.uploadConsent && (
          <UploadConsent
            connectionId={connection.id}
            open={modals.uploadConsent}
            onClose={() => toggleModal("uploadConsent", false)}
          />
        )}

        <div
          className=""
          style={{ display: "flex", alignItems: "center", gap: "15px" }}
        >
          {/* Status Messages */}
          {statusMessages
            .filter((message) => message.show)
            .map((message, index) => (
              <span
                key={`status-${index}`}
                className={Styles.waitingApprovalText}
              >
                {message.text}
              </span>
            ))}

          {/* Action Buttons */}

          {buttons
            .filter((button) => button.show)
            .map((button, index) => (
              <button
                disabled={button.text === "Withdraw" && withdrawLoading}
                key={`right-btn-${index}`}
                onClick={button.onClick}
                className={
                  button.text === "Withdraw"
                    ? Styles.withdrawBtn
                    : Styles.rightSideBtn
                }
              >
                {button.text === "Withdraw" && withdrawLoading ? (
                  <span>Withdrawing...</span>
                ) : (
                  button.text
                )}
              </button>
            ))}
        </div>
      </>
    );
  }
);

const filterMatchSlotsWithFinalizedValues = (data) => {
  if (!data || !data.matches) return data;

  return {
    ...data,
    matches: data.matches.map((match) => ({
      ...match,
      generators:
        match.generators?.map((generator) => ({
          ...generator,
          matchSlots:
            generator.matchSlots?.filter(
              (slot) => slot.finalizedConnectionValues
            ) || [],
        })) || [],
      consumers:
        match.consumers?.map((consumer) => ({
          ...consumer,
          matchSlots:
            consumer.matchSlots?.filter(
              (slot) => slot.finalizedConnectionValues
            ) || [],
        })) || [],
    })),
  };
};

const LedgerFinalTc = ({
  data,
  contractIds,
  result,
  requisition,
  handleSelectedBids,
  handleReq,
  activeSubTab,
  activeListingStatus,
  setHideBucketFill,
  setTermsModal,
  isEditTerms,
  setIsEditTerms,
}) => {
  // const [chatOpen, setChatOpen] = useState(false);
  let normalizedData = Array.isArray(data) ? data[0] : data;

  // Filter the normalized data to only include matchSlots with finalizedConnectionValues
  normalizedData = useMemo(
    () => filterMatchSlotsWithFinalizedValues(normalizedData),
    [normalizedData]
  );

  const user = useSelector(
    (state) => state.persistedReducer.user?.activeRole
  )?.toLowerCase();
  const location = useLocation();
  const dispatch = useDispatch();
  const [expanded, setExpanded] = useState(0);
  const roleDataKey = user === "consumer" ? "generators" : "consumers";
  const uplevelRoleDataKey =
    user === "consumer" ? "capacityConsumer" : "capacityGenerator";
  const uplevelRoleDataKeyReverse =
    user === "consumer" ? "capacityGenerator" : "capacityConsumer";

  // Add the missing onGoingData selector
  const onGoingData = useSelector(
    (state) => state?.CapacitySlice?.activeConnection
  );

  // Add initialization state tracking
  const [isInitialized, setIsInitialized] = useState(false);

  const id = location?.search?.split("=")[1];

  useEffect(() => {
    dispatch(fetchMainScreenActiveConnection({ capId: id }));
  }, [dispatch, id]);

  const mainScreenMatchData = useSelector(
    (state) => state?.CapacitySlice?.mainScreenActiveConnection
  );

  const handleAccordionChange = (val) => {
    if (val === expanded) {
      setExpanded("");
    } else {
      setExpanded(val);
    }
  };

  // Add the missing handleCompanyToggle function
  const handleCompanyToggle = (counter, selfData, accordionIndex) => {
    const companyMatchSlots = counter?.matchSlots || [];

    // Check if all slots for this company in this accordion are selected
    const allSelected = companyMatchSlots.every((slot) =>
      requisition?.some(
        (req) =>
          req?.uniqueId === `${selfData?.id}-${slot?.id}-${accordionIndex}`
      )
    );

    if (allSelected) {
      // Deselect all slots for this company in this accordion
      const slotsToDeselect = [];
      const updatedRequisition = requisition?.filter((ele) => {
        const shouldRemove = companyMatchSlots.some(
          (slot) =>
            ele?.uniqueId === `${selfData?.id}-${slot?.id}-${accordionIndex}`
        );
        if (shouldRemove) {
          const slot = companyMatchSlots.find(
            (s) =>
              ele?.uniqueId === `${selfData?.id}-${s?.id}-${accordionIndex}`
          );
          if (slot) {
            slotsToDeselect.push({
              ...slot,
              company: counter?.companyName,
              consumerGeneratorBidId: counter?.bidId,
              selected: false,
              uniqueId: ele?.uniqueId,
            });
          }
        }
        return !shouldRemove;
      });

      // Update requisition state once
      handleReq(updatedRequisition);

      // Update selectedBids for all deselected slots
      slotsToDeselect.forEach((slot) => {
        handleSelectedBids(slot);
      });
    } else {
      // Select all slots for this company in this accordion
      const newRequisitions = [];
      const newSelectedBids = [];

      companyMatchSlots.forEach((slot, slotIndex) => {
        const uniqueId = `${selfData?.id}-${slot?.id}-${accordionIndex}`;
        const exists = requisition?.some((ele) => ele?.uniqueId === uniqueId);

        if (!exists) {
          const reqPayload = {
            compId: counter?.companyId,
            id: slot?.id,
            uniqueId: uniqueId,
            self: selfData,
            counter: slot,
          };

          newRequisitions.push(reqPayload);

          newSelectedBids.push({
            ...slot,
            company: counter?.companyName,
            consumerGeneratorBidId: counter?.bidId,
            selected: true,
            selfRequisitionIndex: `${accordionIndex}-${slotIndex}`,
            uniqueId,
          });
        }
      });

      // Update requisition state once with all new requisitions
      if (newRequisitions.length > 0) {
        const updatedRequisition = [...(requisition || []), ...newRequisitions];
        handleReq(updatedRequisition);

        // Update selectedBids for all selected slots
        newSelectedBids.forEach((bid) => {
          handleSelectedBids(bid);
        });
      }
    }
  };

  // Add the missing isCompanyFullySelected function
  const isCompanyFullySelected = (counter, selfData, accordionIndex) => {
    const companyMatchSlots = counter?.matchSlots || [];
    if (companyMatchSlots.length === 0) return false;

    return companyMatchSlots.every((slot) =>
      requisition?.some(
        (req) =>
          req?.uniqueId === `${selfData?.id}-${slot?.id}-${accordionIndex}`
      )
    );
  };

  // Add the missing handleUserSelectBid function
  const handleUserSelectBid = ({
    index,
    counterData,
    counterDetails,
    selfData,
    accordionIndex,
  }) => {
    let self_Data = selfData;
    let counter_Data = counterData;

    // Create a unique identifier combining requisition, match slot, and accordion index
    const uniqueId = `${selfData?.id}-${counterData?.id}-${accordionIndex}`;

    const exists = requisition?.some((ele) => ele?.uniqueId === uniqueId);

    if (exists) {
      // Remove the existing one
      const updatedRequisition = requisition?.filter(
        (ele) => ele?.uniqueId !== uniqueId
      );
      handleReq(updatedRequisition);

      handleSelectedBids({
        ...counterData,
        company: counterDetails?.companyName,
        consumerGeneratorBidId: counterDetails?.bidId,
        selected: false,
        uniqueId,
      });
    } else {
      const reqPayload = {
        compId: counterDetails?.companyId,
        id: counter_Data?.id,
        uniqueId: uniqueId,
        self: self_Data,
        counter: counter_Data,
      };

      const updatedRequisition = [...(requisition || []), reqPayload];
      handleReq(updatedRequisition);

      handleSelectedBids({
        ...counterData,
        company: counterDetails?.companyName,
        consumerGeneratorBidId: counterDetails?.bidId,
        selected: true,
        selfRequisitionIndex: index,
        uniqueId,
      });
    }
  };

  useEffect(() => {
    if (
      !normalizedData?.matches ||
      normalizedData.matches.length === 0 ||
      isInitialized
    )
      return;

    const initialRequisitions = [];
    const initialSelectedBids = [];
    let hasConnectionIds = false;

    normalizedData.matches.forEach((ele, accordionIndex) => {
      const selfData = ele?.selfSlot;
      const counterData =
        user === "consumer" ? ele?.generators : ele?.consumers;

      counterData?.forEach((counter) => {
        counter?.matchSlots?.forEach((elem, slotIndex) => {
          const hasConnectionId =
            elem?.connectionId || elem?.finalizedConnectionValues?.id;

          if (hasConnectionId) {
            hasConnectionIds = true;
            const uniqueId = `${selfData?.id}-${elem?.id}-${accordionIndex}`;

            const reqPayload = {
              compId: counter?.companyId,
              id: elem?.id,
              uniqueId: uniqueId,
              self: selfData,
              counter: elem,
            };
            initialRequisitions.push(reqPayload);

            const bidPayload = {
              ...elem,
              company: counter?.companyName,
              consumerGeneratorBidId: counter?.bidId,
              selected: true,
              selfRequisitionIndex: `${accordionIndex}-${slotIndex}`,
              uniqueId,
            };
            initialSelectedBids.push(bidPayload);
          }
        });
      });
    });

    if (hasConnectionIds && initialRequisitions.length > 0) {
      handleReq(initialRequisitions);

      initialSelectedBids.forEach((bid) => {
        handleSelectedBids(bid);
      });

      setIsInitialized(true);
    }
  }, [
    normalizedData.matches,
    isInitialized,
    user,
    handleReq,
    handleSelectedBids,
  ]);

  // Reset initialization when data changes
  useEffect(() => {
    setIsInitialized(false);
  }, [normalizedData?.matches?.length]);

  const isInitiator = onGoingData?.[uplevelRoleDataKey]?.initiatorType === user;

  const isAllReqAccepted = (data) => {
    return data?.matchSlots
      ?.filter((ele) => ele?.finalizedConnectionValues) // Only those with finalizedConnectionValues
      .every((ele) => ele.finalizedConnectionValues.status === "ACCEPTED");
  };

  const initiator = onGoingData?.[roleDataKey]?.initiatorType;

  function shouldReturnNullForTab({ data, user, activeSubTab }) {
    if (activeSubTab === "T&C Approval Requests") {
      // If ALL generators have initiatorType === user, return true (should return null)
      return data?.every((g) => g.initiatorType === user);
    }
    if (activeSubTab === "T&C to initiate") {
      // If ALL generators have initiatorType !== user, return true (should return null)
      return data?.every((g) => g.initiatorType !== user);
    }
    return false;
  }

  const BeforeFinalise = () => {
    useEffect(() => {
      setHideBucketFill(false);
    }, []);
    return (
      <div className={Styles.accordionSummaryContainer}>
        {normalizedData?.matches?.map((ele, index) => {
          const noneFinalized = ele?.[
            user === "consumer" ? "generators" : "consumers"
          ]?.every((consumer) =>
            consumer?.matchSlots?.every(
              (slot) => !slot?.finalizedConnectionValues
            )
          );

          if (
            shouldReturnNullForTab({
              data: ele?.[user === "consumer" ? "generators" : "consumers"],
              user,
              activeSubTab,
            })
          ) {
            return null;
          }

          if (noneFinalized) {
            return null;
          }

          let selfData = ele?.selfSlot;
          let counterData =
            user === "consumer" ? ele?.generators : ele?.consumers;

          // Calculate total requisition quantity
          const totalReqQuantity =
            Number(selfData?.purchaseEnergy || selfData?.sellEnergy) || 0;

          // Calculate selected quantity for this requisition
          let selectedQuantity = 0;
          if (!onGoingData?.length && initiator === user) {
            selectedQuantity = counterData?.reduce((sum, counter) => {
              return (
                sum +
                (counter?.matchSlots?.reduce((slotSum, elem) => {
                  return (
                    slotSum +
                    (Number(elem?.purchaseEnergy || elem?.sellEnergy) || 0)
                  );
                }, 0) || 0)
              );
            }, 0);
          } else {
            selectedQuantity = counterData?.reduce((sum, counter) => {
              return (
                sum +
                (counter?.matchSlots?.reduce((slotSum, elem) => {
                  const isSelected = requisition?.some(
                    (req) =>
                      req?.uniqueId === `${selfData?.id}-${elem?.id}-${index}`
                  );
                  return (
                    slotSum +
                    (isSelected
                      ? Number(elem?.purchaseEnergy || elem?.sellEnergy) || 0
                      : 0)
                  );
                }, 0) || 0)
              );
            }, 0);
          }

          // Calculate bucket fill percentage
          const bucketFillPercent =
            totalReqQuantity > 0
              ? Math.round((selectedQuantity / totalReqQuantity) * 100)
              : 0;

          return (
            <>
              <div
                className={
                  expanded === index
                    ? Styles.detailsMainDrop
                    : Styles.detailsMainDrop_inactive
                }
              >
                <Accordion
                  style={{
                    marginBottom: "5px",

                    border:
                      expanded === index
                        ? "1px solid var(--primary-color-text)"
                        : "none",
                  }}
                  expanded={expanded === index}
                  onChange={() => handleAccordionChange(index)}
                >
                  <AccordionSummary
                    style={{
                      borderBottom: "var(--border-level2)",
                      borderRadius: 10,
                      borderRadiusBottom: 0,
                    }}
                  >
                    <table className={Styles.ledgerTableHeading}>
                      <thead>
                        <tr>
                          <th
                            style={{
                              textAlign: "left",
                              marginTop: "10px",
                              width: user === "consumer" ? "16.6%" : "20%",
                              display: "flex",
                              alignItems: "center",
                            }}
                            className=""
                          >
                            <ExpandMoreIcon
                              style={{
                                marginRight: "20px",
                                marginLeft: "30px",
                                fontSize: "18px",
                              }}
                              className={`${Styles.expandIconLeft} ${
                                expanded === index ? Styles.rotateIcon : ""
                              }`}
                            />
                            {index + 1}
                          </th>
                          <th
                            className=""
                            style={{
                              width:
                                user === USER_TYPES.CONSUMER ? "16.66%" : "20%",
                            }}
                          >
                            {ele?.selfSlot?.purchaseEnergy ||
                              ele?.selfSlot?.sellEnergy}{" "}
                            (MW)
                          </th>
                          <th
                            className=""
                            style={{
                              width:
                                user === USER_TYPES.CONSUMER ? "16.66%" : "20%",
                            }}
                          >
                            {ele?.selfSlot?.energyRateStr ||
                              ele?.selfSlot?.landingCost ||
                              ele?.selfSlot?.sellingCost}{" "}
                            (₹/kWh)
                          </th>
                          <th
                            className=""
                            style={{
                              width:
                                user === USER_TYPES.CONSUMER ? "16.66%" : "20%",
                            }}
                          >
                            {formatDate(ele?.selfSlot?.dateStartAt)}-
                            {formatDate(ele?.selfSlot?.dateEndAt)}
                          </th>
                          <th
                            className=""
                            style={{
                              textAlign:
                                user === "consumer" ? "right" : "center",
                              width:
                                user === USER_TYPES.CONSUMER ? "16.66%" : "20%",
                            }}
                          >
                            {formatSlotTime(ele?.selfSlot?.slotStartAt)}-
                            {formatSlotTime(ele?.selfSlot?.slotEndAt)}
                          </th>
                          {user === "consumer" && (
                            <th
                              style={{
                                textAlign: "right",
                                width: user === "consumer" ? "16.6%" : "20%",
                              }}
                            >
                              <div
                                style={{
                                  position: "relative",
                                  left: "-56px",
                                  display: "flex",
                                  justifyContent: "space-between",
                                }}
                              >
                                <CircularProgress
                                  variant="determinate"
                                  value={
                                    bucketFillPercent > 100
                                      ? 100
                                      : bucketFillPercent
                                  }
                                  size={40}
                                  thickness={4}
                                  style={{
                                    position: "absolute",
                                    right: "-5px",
                                    top: "-20px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "end",
                                    color: "#00d26a",
                                    background: "transparent",
                                  }}
                                />
                                <div
                                  style={{
                                    position: "absolute",
                                    top: 0,
                                    left: 0,
                                    width: "100%",
                                    height: "100%",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "end",
                                    fontWeight: 500,
                                    fontSize: "12px",
                                    color: "var(--sixth-color-text)",
                                  }}
                                >
                                  {bucketFillPercent}%
                                </div>
                              </div>
                            </th>
                          )}
                        </tr>
                      </thead>
                    </table>
                  </AccordionSummary>
                  <AccordionDetails>
                    <>
                      {ele?.[roleDataKey]?.map((counter, p) => {
                        return (
                          <div className={Styles.accordionDetailsContainer}>
                            {p === 0 && (
                              <div className={Styles.detailsHeaderText}>
                                Counterparty Requisition
                              </div>
                            )}
                            <div
                              style={{
                                backgroundColor:
                                  (user === "consumer" &&
                                    counter?.initiatorType === "generator") ||
                                  (user === "generator" &&
                                    counter?.initiatorType === "consumer")
                                    ? "#FEBC2F14"
                                    : "",
                                paddingRight:
                                  user === USER_TYPES.CONSUMER
                                    ? "47px"
                                    : "33px",
                              }}
                              className={`display_flex_row ${Styles.accHeaderTable_name}`}
                            >
                              <div>
                                <div
                                  className={`table_text_align_left ${Styles.nameCompanyDetails}`}
                                >
                                  <div>
                                    {counter?.companyDisplayName ||
                                      counter?.companyName}
                                  </div>
                                  <div>{counter?.name}</div>
                                </div>

                                {((user === "consumer" &&
                                  counter?.initiatorType === "generator") ||
                                  (user === "generator" &&
                                    counter?.initiatorType === "consumer")) && (
                                  <div
                                    style={{
                                      textWrap: "nowrap",
                                      color: "var(--primary-color-text)",
                                      marginLeft: "40px",
                                      fontWeight: 500,
                                      fontSize: "10px",
                                    }}
                                  >
                                    T&C Request received
                                  </div>
                                )}
                              </div>

                              <div style={{ width: "15%" }}></div>
                              <div style={{ width: "20%" }}></div>
                              <div style={{ width: "25%" }}></div>
                              <div
                                style={{ width: "15%" }}
                                className="table_text_align_right"
                              >
                                <div
                                  className="display_flex_row"
                                  style={{
                                    justifyContent: "end",
                                    alignItems: "center",
                                    gap: "15px",
                                  }}
                                >
                                  <div className={Styles.chatButtonContainer}>
                                    <button
                                      type="button"
                                      className={Styles.chatButton}
                                      onClick={() =>
                                        openBilateralChat(dispatch, {
                                          header: counter?.name,
                                          fromUser:
                                            onGoingData[0][uplevelRoleDataKey]
                                              ?.name,
                                          toUser: counter?.name,
                                        })
                                      }
                                    >
                                      Chat
                                    </button>
                                  </div>
                                  <div>
                                    {onGoingData[uplevelRoleDataKey]
                                      ?.initiatorType !== user && (
                                      <AntSwitch
                                        disabled={isAllReqAccepted(counter)}
                                        checked={
                                          isAllReqAccepted(counter) ||
                                          isCompanyFullySelected(
                                            counter,
                                            selfData,
                                            index
                                          )
                                        }
                                        onChange={() =>
                                          handleCompanyToggle(
                                            counter,
                                            selfData,
                                            index
                                          )
                                        }
                                      />
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                            {counter?.matchSlots?.map((elem, i) => {
                              if (!elem?.finalizedConnectionValues) return null;
                              return (
                                <>
                                  <table className={Styles.ledgerTableHeading}>
                                    <thead style={{ height: "45px" }}>
                                      <tr style={{ position: "relative" }}>
                                        <th
                                          style={{
                                            textAlign: "left",
                                            paddingLeft: "25px",
                                            width:
                                              user === "consumer"
                                                ? "16.6%"
                                                : "20%",
                                            color: "#8C8A96",
                                          }}
                                          className=""
                                        >
                                          Requisition {i + 1}
                                        </th>
                                        <th
                                          className=""
                                          style={{
                                            width:
                                              user === USER_TYPES.CONSUMER
                                                ? "16.66%"
                                                : "20%",
                                          }}
                                        >
                                          {
                                            elem?.finalizedConnectionValues
                                              ?.quantity
                                          }{" "}
                                          (MW)
                                        </th>
                                        <th
                                          className=""
                                          style={{
                                            width:
                                              user === USER_TYPES.CONSUMER
                                                ? "16.66%"
                                                : "20%",
                                          }}
                                        >
                                        
                                          {elem?.finalizedConnectionValues
                                            ?.energyRateType === "FIXED"
                                            ? `${elem?.finalizedConnectionValues?.amount}`
                                            : elem?.finalizedConnectionValues
                                                ?.energyRateType === "DAM_PLUS"
                                            ? `DAM+${elem?.finalizedConnectionValues?.amount}`
                                            : `DAM-${elem?.finalizedConnectionValues?.amount}`}
                                          (₹/kWh)
                                        </th>
                                        <th
                                          className=""
                                          style={{
                                            width:
                                              user === USER_TYPES.CONSUMER
                                                ? "16.66%"
                                                : "20%",
                                          }}
                                        >
                                          {formatDate(
                                            elem?.finalizedConnectionValues
                                              ?.dateStartAt
                                          )}{" "}
                                          -{" "}
                                          {formatDate(
                                            elem?.finalizedConnectionValues
                                              ?.dateEndAt
                                          )}
                                        </th>
                                        <th
                                          className=""
                                          style={{
                                            textAlign:
                                              user === USER_TYPES.CONSUMER
                                                ? "right"
                                                : "center",
                                            width:
                                              user === USER_TYPES.CONSUMER
                                                ? "16.6%"
                                                : "20%",
                                          }}
                                        >
                                          {formatSlotTime(
                                            elem?.finalizedConnectionValues
                                              ?.slotStartAt
                                          )}
                                          -
                                          {formatSlotTime(
                                            elem?.finalizedConnectionValues
                                              ?.slotEndAt
                                          )}
                                        </th>
                                        {normalizedData[uplevelRoleDataKey]
                                          .initiatorType !== user && (
                                          <th
                                            className=""
                                            style={{
                                              position:
                                                user === USER_TYPES.CONSUMER
                                                  ? "reltive"
                                                  : "reltive",
                                              bottom: "12px",
                                              width:
                                                user === USER_TYPES.CONSUMER
                                                  ? "16.6%"
                                                  : "0%",
                                            }}
                                          >
                                            <div
                                              style={{
                                                position: "absolute",
                                                top: "15px",
                                                left:
                                                  user === USER_TYPES.CONSUMER
                                                    ? "-48px"
                                                    : "-34px",
                                                display: "flex",
                                                width: "100%",
                                                justifyContent: "end",
                                              }}
                                            >
                                              <AntSwitch
                                                disabled={
                                                  ele?.[roleDataKey][p]
                                                    ?.matchSlots[i]
                                                    ?.finalizedConnectionValues
                                                    ?.status === "ACCEPTED"
                                                }
                                                checked={
                                                  ele?.[roleDataKey][p]
                                                    ?.matchSlots[i]
                                                    ?.finalizedConnectionValues
                                                    ?.status === "ACCEPTED" ||
                                                  requisition?.filter(
                                                    (ele) =>
                                                      ele?.uniqueId ===
                                                      `${selfData?.id}-${elem?.id}-${index}`
                                                  )?.length > 0
                                                }
                                                onChange={() =>
                                                  handleUserSelectBid({
                                                    index: `${index}-${i}`,
                                                    counterData: elem,
                                                    counterDetails: counter,
                                                    selfData: selfData,
                                                    accordionIndex: index,
                                                  })
                                                }
                                              />
                                            </div>
                                          </th>
                                        )}
                                      </tr>
                                    </thead>
                                  </table>
                                </>
                              );
                            })}
                          </div>
                        );
                      })}
                    </>
                  </AccordionDetails>
                </Accordion>
              </div>
            </>
          );
        })}
      </div>
    );
  };

  const AfterFinalizeContracts = () => {
    useEffect(() => {
      setHideBucketFill(true);
    }, []);
    return (
      <>
        {mainScreenMatchData?.map((ele, index) => {
          return (
            <>
              <Accordion
                style={{
                  backgroundColor: "#f3f5f8",
                }}
                expanded={expanded === index}
                onChange={() => handleAccordionChange(index)}
              >
                <AccordionDetails
                  style={{ backgroundColor: "white", borderRadius: "8px" }}
                >
                  <>
                    <div className={Styles.accordionDetailsContainerUploadDocs}>
                      <div
                        style={{ paddingRight: "15px" }}
                        className={`display_flex_row ${Styles.accHeaderTable_name}`}
                      >
                        <div
                          style={{ marginLeft: "0.7%" }}
                          className={`table_text_align_left ${Styles.nameCompanyDetails}`}
                        >
                          <div>
                            {ele?.[uplevelRoleDataKeyReverse]
                              ?.companyDisplayName ||
                              ele?.[uplevelRoleDataKeyReverse]?.consumerName ||
                              ele?.[uplevelRoleDataKeyReverse]?.generatorName}
                          </div>
                          <div>{ele?.[uplevelRoleDataKeyReverse]?.name}</div>
                        </div>
                        <div className={Styles.chatButtonContainer}>
                          <button
                            style={{ marginRight: "calc(100vw - 95vw)" }}
                            type="button"
                            className={Styles.chatButton}
                            onClick={() =>
                              openBilateralChat(dispatch, {
                                header: ele?.[uplevelRoleDataKeyReverse]?.name,
                                fromUser:
                                  onGoingData[0][uplevelRoleDataKey]?.name,
                                toUser: ele?.[uplevelRoleDataKeyReverse]?.name,
                              })
                            }
                          >
                            Chat
                          </button>
                        </div>
                      </div>
                      {ele?.bilateralConnections?.map((elem, i) => {
                        if (elem.status === "REJECTED") return null;
                        return (
                          <table className={Styles.ledgerTableHeading}>
                            <tbody>
                              <tr style={{ height: "40px" }}>
                                <th
                                  style={{
                                    textAlign: "left",
                                    paddingLeft: "30px",
                                    width:
                                      user === "consumer" ? "16.6%" : "20%",
                                    color: "#8C8A96",
                                  }}
                                  className=""
                                >
                                  Connection {i + 1}
                                </th>
                                <th
                                  className=""
                                  style={{
                                    width:
                                      user === USER_TYPES.CONSUMER
                                        ? "16.6%"
                                        : "20%",
                                  }}
                                >
                                  {elem?.quantity} (MW)
                                </th>
                                <th
                                  className=""
                                  style={{
                                    width:
                                      user === USER_TYPES.CONSUMER
                                        ? "16.6%"
                                        : "20%",
                                  }}
                                >
                                  {elem?.energyRateType === "FIXED"
                                    ? `${elem?.amount} (₹/kWh)`
                                    : elem?.energyRateType === "DAM_PLUS"
                                    ? `DAM+${elem?.amount}`
                                    : `DAM-${elem?.amount}`}
                                </th>
                                <th
                                  className=""
                                  style={{
                                    width:
                                      user === USER_TYPES.CONSUMER
                                        ? "16.6%"
                                        : "20%",
                                  }}
                                >
                                  {formatDate(elem?.dateStartAt)} -{" "}
                                  {formatDate(elem?.dateEndAt)}
                                </th>
                                <th
                                  className=""
                                  style={{
                                    width:
                                      user === USER_TYPES.CONSUMER
                                        ? "16.6%"
                                        : "20%",
                                  }}
                                >
                                  {formatSlotTime(elem?.slotStartAt)}-
                                  {formatSlotTime(elem?.slotEndAt)}
                                </th>
                              </tr>
                            </tbody>
                          </table>
                        );
                      })}

                      <div
                        style={{ paddingRight: "calc(100vw - 95vw)" }}
                        className={Styles.statusBtnContainer}
                      >
                        <LeftSideBtns clickedIndex={index} />
                        <RightSideBtn
                          result={result}
                          activeListingStatus={activeListingStatus}
                          clickedIndex={index}
                          setTermsModal={setTermsModal}
                          isEditTerms={isEditTerms}
                          setIsEditTerms={setIsEditTerms}
                        />
                      </div>
                    </div>
                  </>
                </AccordionDetails>
              </Accordion>
            </>
          );
        })}
      </>
    );
  };

  return (
    <>
      {(result.allAreGenerator && user === "generator") ||
      (result.allAreConsumer && user === "consumer") ||
      normalizedData?.status === "PARTIAL_FINAL" ? (
        <AfterFinalizeContracts />
      ) : (result.allAreGenerator && user === "consumer") ||
        (result.allAreConsumer && user === "generator") ? (
        <BeforeFinalise />
      ) : null}
    </>
  );
};

export default LedgerFinalTc;
